/**
 * VEIEN FRA NOE SOM IKKE ER EN KOMPONENT, TIL EN TOAST.
 *
 * ================================================================================================
 * HVORFOR DETTE FINNES (QA13 N2, 20. september).
 *
 * «`POST /v1/departments/default-pack` svarer 503. Ingen toast, ingen feilmelding, ingen endring.
 * Knappen ser død ut.»
 *
 * Ruta var ødelagt, og det var den ene halvparten. Den andre var verre: `MutationCache` i
 * `query-client.ts` hadde `onSuccess` og ingen `onError`, så ENHVER mutasjon uten sin egen
 * `onError` feilet i stillhet — i hele appen, ikke bare på den knappen. En knapp som ikke gjør noe
 * og ikke sier noe er den verste tilstanden et grensesnitt kan ha: den som trykker prøver igjen,
 * og igjen, og tror det er hen som gjør noe feil.
 *
 * `MutationCache` lages utenfor React og kan derfor ikke kalle `useToast`. Broen er dette: én
 * modulvariabel, satt av `ToastProvider` mens den er montert, lest av cachen. Ikke et mønster som
 * skal spre seg — dette er det ene stedet i appen som står utenfor treet og likevel må si fra.
 *
 * Før provideren er montert (og i en test som rendrer uten den) er `emit` en tom funksjon, ikke et
 * kast: en feilmelding som selv feiler er ikke en forbedring.
 */
import type { ToastOptions } from './Toast';

type Sink = (options: ToastOptions) => void;

let sink: Sink | null = null;
let shown = 0;

/** `ToastProvider` melder seg på mens den er montert, og av igjen når den ikke er det. */
export function setToastSink(next: Sink | null): void {
  sink = next;
}

/** Vis en toast fra et sted som ikke er en komponent. Gjør ingenting hvis ingen lytter. */
export function emitToast(options: ToastOptions): void {
  shown += 1;
  sink?.(options);
}

/**
 * HVOR MANGE TOASTS SOM ER VIST, noensinne. Teller, ikke tidsstempel.
 *
 * Den globale `onError` bruker den til å holde kjeft når noen andre alt har sagt fra: den leser
 * tallet når feilen skjer, venter et tikk, og lar være hvis tallet har flyttet seg. Halvparten av
 * mutasjonene i appen viser sin egen, presise feilmelding ved knappen; den skal vinne over «Noe
 * gikk galt», og to meldinger om den samme handlingen er verre enn ingen av delene.
 *
 * En teller og ikke en klokke fordi spørsmålet er «kom det noe?», ikke «hvor lenge siden».
 */
export function toastsShown(): number {
  return shown;
}

/**
 * EN FEIL SOM ER VIST ET ANNET STED ENN I EN TOAST — i en celle i hurtigredigering.
 *
 * `MutationCache` i `app/query-client.ts` viser en toast for hver mutasjon som feiler uten å ha sagt
 * fra, og måler «har noen sagt fra?» på dette tallet. Cella som viser avvisningen teller seg selv
 * med her, så leseren ikke får den samme feilen to steder. Ingen toast tegnes.
 */
export function noteErrorShown(): void {
  shown += 1;
}

/**
 * EN TOAST FRA EN KOMPONENT (`useToast`) TELLER OGSÅ (QA6 K5, L2). Uten den så den globale
 * `onError` aldri at `mutate(v, { onError: () => toast(…) })` hadde sagt fra, og la «Noe gikk galt»
 * oppå «Fikk ikke delt» — to toaster for samme svar. `ToastProvider` kaller den for hver toast.
 */
export function noteToastShown(): void {
  shown += 1;
}
